import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ListPlus, Check, Plus, Loader2 } from "lucide-react";
import { fetchPlaylists, createPlaylist, updatePlaylist } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PlaylistAdder = ({ song }) => {
  const { user, openAuth } = useAuth();
  const [open, setOpen] = useState(false);
  const [playlists, setPlaylists] = useState(null);
  const [newName, setNewName] = useState("");
  const [flash, setFlash] = useState("");
  const [busy, setBusy] = useState(false);

  const openMenu = async () => {
    if (!user) {
      openAuth("signup");
      return;
    }
    setOpen((v) => !v);
    if (!open) {
      setFlash("");
      setPlaylists(null);
      setPlaylists(await fetchPlaylists().catch(() => []));
    }
  };

  const addedFlash = (msg) => {
    setFlash(msg);
    setTimeout(() => setFlash(""), 2000);
  };

  const addTo = async (pl) => {
    if (pl.song_ids.includes(song.song_id)) {
      addedFlash(`Already in "${pl.name}"`);
      return;
    }
    setBusy(true);
    try {
      const updated = await updatePlaylist(pl.playlist_id, {
        song_ids: [...pl.song_ids, song.song_id],
      });
      setPlaylists((ps) => ps.map((p) => (p.playlist_id === pl.playlist_id ? updated : p)));
      addedFlash(`Added to "${pl.name}"`);
    } finally {
      setBusy(false);
    }
  };

  const createNew = async (e) => {
    e.preventDefault();
    if (!newName.trim() || busy) return;
    setBusy(true);
    try {
      const pl = await createPlaylist({ name: newName.trim(), song_ids: [song.song_id] });
      setPlaylists((ps) => [...(ps || []), pl]);
      setNewName("");
      addedFlash(`Saved to new playlist "${pl.name}"`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="relative">
      <button
        onClick={openMenu}
        data-testid={`song-playlist-${song.song_id}`}
        aria-label={`Add ${song.title} to a playlist`}
        className="grid h-8 w-8 place-items-center rounded-full text-slate-300 transition-colors hover:bg-[#E0F2FE] hover:text-[#0284C7]"
      >
        <ListPlus className="h-[18px] w-[18px]" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            className="absolute right-0 z-30 mt-2 w-64 rounded-2xl border border-[#E2DDD0] bg-white p-3 shadow-xl"
            data-testid={`playlist-menu-${song.song_id}`}
          >
            <p className="px-2 pb-2 text-xs font-bold uppercase tracking-wider text-slate-400">
              Save to playlist
            </p>
            {flash && (
              <p className="mx-2 mb-2 flex items-center gap-1.5 rounded-xl bg-[#DCFCE7] px-3 py-2 text-xs font-bold text-[#16A34A]" data-testid={`playlist-flash-${song.song_id}`}>
                <Check className="h-3.5 w-3.5" /> {flash}
              </p>
            )}
            <div className="max-h-40 space-y-1 overflow-y-auto">
              {playlists === null ? (
                <Loader2 className="mx-auto my-3 h-5 w-5 animate-spin text-slate-300" />
              ) : playlists.length === 0 ? (
                <p className="px-2 py-2 text-xs text-slate-400">No playlists yet — create one below.</p>
              ) : (
                playlists.map((pl) => (
                  <button
                    key={pl.playlist_id}
                    onClick={() => addTo(pl)}
                    disabled={busy}
                    data-testid={`playlist-add-${pl.playlist_id}`}
                    className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm font-semibold text-slate-700 transition-colors hover:bg-[#F8F5EC]"
                  >
                    {pl.name}
                    <span className="text-xs font-bold text-slate-400">{pl.song_ids.length} songs</span>
                  </button>
                ))
              )}
            </div>
            <form onSubmit={createNew} className="mt-2 flex gap-1.5 border-t border-[#F8F5EC] pt-2">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                maxLength={60}
                data-testid={`playlist-new-name-${song.song_id}`}
                placeholder="New playlist name"
                className="min-w-0 flex-1 rounded-xl border border-[#E2DDD0] bg-[#F8F5EC] px-3 py-2 text-sm outline-none focus:border-[#FFC107]"
              />
              <button
                type="submit"
                disabled={busy || !newName.trim()}
                data-testid={`playlist-create-${song.song_id}`}
                aria-label="Create playlist"
                className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-slate-900 text-white disabled:opacity-40"
              >
                <Plus className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </span>
  );
};

export default PlaylistAdder;
